import {useState} from 'react';
import {DialogClose, DialogContent, DialogOverlay, DialogPortal} from '@radix-ui/react-dialog';
import NextLink from 'next/link';

import {Avatar, Icon, TallyLogo} from '#component/base';
import {HeaderItemProps} from '#component/partial/Header/components/HeaderItem/HeaderItem.types';
import * as headerCss from '#component/partial/Header/Header.css';
import {HeaderLink, miscLinks, navLinks} from '#component/partial/Header/Header.util';
import {AccordionRoot} from '#component/util';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {HeaderItem} from '../HeaderItem';
import {MobileAccordion} from '../MobileAccordion';

import {MobileMenuProps} from './MobileMenu.types';

import * as css from './MobileMenu.css';

type RenderMenuOptions = {
  isAuthenticated: boolean;
  hasActiveSubscription: boolean;
  hasMembership: boolean;
  featureFlagMap: Record<string, boolean>;
  onItemClick?: () => void;
  openIndex?: string;
  variant: HeaderItemProps['variant'];
};

const renderMenu = (links: HeaderLink[], options: RenderMenuOptions) => {
  return links.map(
    (
      {
        requiresAuth,
        requiresAnon,
        subMenu,
        requiresActiveSubscription,
        requiresMembership,
        memberLock,
        featureFlag,
        ...restProps
      },
      index,
    ) => {
      if (
        (requiresAuth === true && !options.isAuthenticated) ||
        (requiresAnon === true && options.isAuthenticated) ||
        (requiresActiveSubscription === true && !options.hasActiveSubscription) ||
        (requiresMembership === true && !options.hasMembership) ||
        (!!featureFlag && !options.featureFlagMap[featureFlag])
      ) {
        return null;
      }

      if (subMenu !== undefined) {
        return (
          <MobileAccordion
            key={index}
            isOpen={options.openIndex === `${index}`}
            trigger={restProps.children}
            value={`${index}`}>
            {renderMenu(subMenu, {...options, variant: 'mobileFooter'})}
          </MobileAccordion>
        );
      } else {
        return (
          <HeaderItem
            key={index}
            onClick={options.onItemClick}
            variant={options.variant}
            locked={memberLock && !options.hasMembership}
            href={restProps.href}
            rel={restProps.rel}
            target={restProps.target}>
            {restProps.children}
          </HeaderItem>
        );
      }
    },
  );
};

export const MobileMenu = ({
  avatar = '',
  initials,
  isAuthenticated = false,
  hasActiveSubscription = false,
  hasMembership = false,
  featureFlagMap = {},
  onClickLink,
}: MobileMenuProps) => {
  const [openIndex, setOpenIndex] = useState('');
  const {isMobile} = useViewportSize();

  return (
    <DialogPortal>
      <DialogOverlay />

      <DialogContent className={css.root}>
        <header className={css.header}>
          <NextLink href="/shop" passHref>
            <DialogClose asChild>
              <a aria-label="Tally Health™" className={headerCss.logo}>
                <TallyLogo color="cotton" size={isMobile ? 40 : 48} sizeType="height" />
              </a>
            </DialogClose>
          </NextLink>

          <div className={sprinkles({flex: 'flex', gap: 16, items: 'center'})}>
            {isAuthenticated && (
              <NextLink href="/profile" passHref>
                <a aria-label="Visit profile" onClick={onClickLink}>
                  <Avatar
                    className={headerCss.avatar}
                    image={avatar}
                    text={initials || '??'}
                    size="smaller"
                  />
                </a>
              </NextLink>
            )}
            <DialogClose className={css.closeButton}>
              <Icon aria-label="Close menu" color="cotton" name="X" size={24} />
            </DialogClose>
          </div>
        </header>

        <div
          className={sprinkles({content: 'between', dir: 'col', flex: 'flex', gap: 20, grow: 1})}>
          <AccordionRoot asChild collapsible onValueChange={setOpenIndex} type="single">
            <nav className={sprinkles({dir: 'col', flex: 'flex', gap: 20, items: 'start', pT: 64})}>
              {renderMenu(navLinks, {
                isAuthenticated,
                hasActiveSubscription,
                hasMembership,
                openIndex,
                variant: 'mobile',
                featureFlagMap,
                onItemClick: onClickLink,
              })}
            </nav>
          </AccordionRoot>

          <footer className={css.footer}>
            {renderMenu(miscLinks, {
              isAuthenticated,
              hasActiveSubscription,
              hasMembership,
              variant: 'mobileFooter',
              featureFlagMap,
              onItemClick: onClickLink,
            })}
          </footer>
        </div>
      </DialogContent>
    </DialogPortal>
  );
};
